<template>
  <div
    v-if="shouldShowMenu"
    class="mini-opt-menu"
    :class="{ 'is-visible': visible }"
    :style="menuStyle"
    ref="menuRef"
    @click.stop
  >
    <el-card shadow="never" class="menu-card">
      <template v-if="menuType === 'level'">
        <div class="menu-item" @click="handleButtonAndHide('insert')">
          <el-icon><Plus /></el-icon>向前插入指标
        </div>
        <div
          class="menu-item"
          @click="handleButtonAndHide('addSibling')"
          v-if="level <= 4"
        >
          <el-icon><Plus /></el-icon>新增同级指标
        </div>
        <div
          class="menu-item"
          @click="handleButtonAndHide('addChild')"
          v-if="canAddChild"
        >
          <el-icon><Plus /></el-icon>新增子级指标
        </div>
        <div class="menu-item" @click="handleButtonAndHide('edit')">
          <el-icon><Edit /></el-icon>编辑指标名
        </div>
        <!-- 删除按钮：当 levelCount 为1时禁用 -->
        <div
          class="menu-item"
          :class="{ 'is-disabled': isDeleteDisabled }"
          @click="!isDeleteDisabled && handleButtonAndHide('del')"
        >
          <el-icon><Delete /></el-icon>删除
        </div>
        <!-- <div class="menu-item" @click="handleButtonAndHide('del')">
          <div>levelCount: {{ levelCount }}</div>
          <el-icon><Delete /></el-icon>删除
        </div> -->
      </template>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  nextTick,
  watch,
} from "vue";
import { Plus, Edit, Delete } from "@element-plus/icons-vue";
import { addLevel, updateLevel, deleteLevel } from "@/api/level";

import { ElMessage, ElMessageBox } from "element-plus";

const props = defineProps<{
  visible: boolean;
  menuType?: string;
  position: { left: number; top: number };
  id?: string;
  standardId?: string;
  level?: number;
  numId?: string;
  name?: string;
  content?: string;
  children?: any[];
  imageList?: any[];
  ifNextLevel?: number;
  menuList?: any[];
  currentItem: {
    type: string;
    isMaxLevel?: boolean;
    insertBefore?: boolean;
    relativeNumId?: string;
    // ... 其他属性
  };
  levelCount?: number; // 新增，主组件传入
  firstLevelCount?: number; // 新增：一级指标的数量
}>();

const emit = defineEmits<{
  "update:visible": [value: boolean];
  "mini-menu": [
    action: {
      type: string;
      data?: any; // 可以传递更新后的数据
    }
  ];
  refresh: [];
}>();

const hideMenu = () => {
  emit("update:visible", false);
};

const handleButtonAndHide = async (type: string) => {
  try {
    if (type === "edit") {
      await handleEdit();
    } else if (type === "del") {
      await handleDelete();
    } else if (type === "insert") {
      const insertBefore = type === "insert";

      emit("mini-menu", {
        type,
        data: {
          parentNumId: props.numId,
          level: props.level,
          // insertBefore: insertBefore,
          insertBefore: true, // 明确传递 true
          relativeNumId: props.currentItem?.numId, // 传递当前项的 numId
        },
      });

      // 在这里添加焦点设置逻辑
      await nextTick(); // 确保DOM更新完成
      const inputElement = document.querySelector(
        `input[data-numid="${insertBefore}"]`
      );
      if (inputElement) {
        inputElement.focus(); // 设置焦点
        inputElement.scrollIntoView({ behavior: "smooth", block: "center" }); // 滚动到视图中
      }
    } else {
      const newNumId = type === "addSibling";
      emit("mini-menu", {
        type,
        data: {
          parentNumId: props.numId,
          level: props.level,
          newNumId: newNumId,
        },
      });

      // 在这里添加焦点设置逻辑
      await nextTick(); // 确保DOM更新完成
      const inputElement = document.querySelector(
        `input[data-numid="${newNumId}"]`
      );
      if (inputElement) {
        inputElement.focus(); // 设置焦点
        inputElement.scrollIntoView({ behavior: "smooth", block: "center" }); // 滚动到视图中
      }
    }
  } finally {
    hideMenu();
  }
};
const isDeleteDisabled = computed(() => {
  if (Number(props.levelCount) === 1) return true;
  if (Number(props.level) === 1 && Number(props.firstLevelCount) === 1)
    return true;
  return false;
});
// // 计算删除按钮是否禁用
// const isDeleteDisabled = computed(() => {
//   // 条件1：整个菜单栏只有一个指标项（无论级别）
//   if (props.levelCount === 1) {
//     return true;
//   }

//   // 条件2：当前指标是一级指标，并且整个菜单栏中一级指标的数量只有一个
//   if (props.level === 1 && props.firstLevelCount === 1) {
//     return true;
//   }
//   // 条件3：当前指标是唯一的一级指标（新增）
//   // 检查当前指标是否是一级指标，并且它是菜单中唯一的一级指标
//   const isOnlyFirstLevelItem =
//     props.level === 1 &&
//     props.firstLevelCount === 1 &&
//     props.menuList.some(
//       (item) =>
//         item.type === "level" && item.level === 1 && item.id === props.id
//     );

//   if (isOnlyFirstLevelItem) {
//     return true;
//   }

//   return false;
// });
// 删除提示信息
const deleteHint = computed(() => {
  if (props.levelCount === 1) {
    return "唯一指标不可删除";
  }

  if (props.level === 1 && props.firstLevelCount === 1) {
    return "唯一一级指标不可删除";
  }

  return "";
});
const handleDelete = async () => {
  try {
    // 双重检查：再次确认是否满足禁用条件
    if (props.levelCount === 1) {
      ElMessage.warning("至少保留一个指标，无法删除！");
      return;
    }

    if (props.level === 1 && props.firstLevelCount === 1) {
      ElMessage.warning("至少保留一个一级指标，无法删除！");
      return;
    }
    if (isDeleteDisabled.value) {
      ElMessage.warning("至少保留一个指标，无法删除！");
      return;
    }
    if (!props.id || !props.level || !props.numId) {
      console.error("删除操作缺少必要参数:", {
        id: props.id,
        level: props.level,
        numId: props.numId,
      });
      ElMessage.error("删除失败：缺少必要参数");
      return;
    }
    // 修改删除接口的调用方式
    const response = await deleteLevel({
      id: props.standardId,
      level: props.level,
      numId: props.numId,
    });

    if (response?.code === 1) {
      ElMessage.success("删除成功");
      emit("mini-menu", { type: "del", data: { id: props.id } });
    } else {
      throw new Error(response?.msg || "删除失败");
    }
  } catch (error) {
    console.error("删除失败:", error);
    ElMessage.error(error instanceof Error ? error.message : "删除失败");
  }
};

const handleEdit = () => {
  if (!props.currentItem) {
    console.error("没有选择需编辑的菜单项");
    return;
  }

  emit("mini-menu", {
    type: "edit",
    data: {
      ...props.currentItem,
      isEdit: true,
    },
  });
};

// 添加辅助函数
const findItemInMenuList = (menuList: any[], targetId: string): any => {
  for (const item of menuList) {
    if (item.id === targetId) return item;
    if (item.children?.length) {
      const found = findItemInMenuList(item.children, targetId);
      if (found) return found;
    }
  }
  return null;
};
const menuRef = ref<HTMLElement | null>(null);

// 添加菜单位置调整逻辑
const adjustMenuPosition = async () => {
  await nextTick();
  if (!menuRef.value) return;

  const menu = menuRef.value;
  const menuRect = menu.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // 计算理想右下角位置
  let x = props.position.x;
  let y = props.position.y;

  // 水平方向调整
  if (x + menuRect.width > viewportWidth) {
    x = props.position.x - menuRect.width; // 向左移动
  }

  // 垂直方向调整
  if (y + menuRect.height > viewportHeight) {
    y = props.position.y - menuRect.height; // 向上移动
  }

  // 边界保护
  x = Math.max(5, Math.min(x, viewportWidth - menuRect.width - 5));
  y = Math.max(5, Math.min(y, viewportHeight - menuRect.height - 5));

  menu.style.left = `${x}px`;
  menu.style.top = `${y}px`;
};

// 监听菜单显示状态
watch(
  () => props.visible,
  async (newVal) => {
    if (newVal) {
      await adjustMenuPosition();
    }
  }
);
// 监听窗口大小变化
onMounted(() => {
  window.addEventListener("resize", adjustMenuPosition);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", adjustMenuPosition);
});

// 计算是否可以添加子级
const canAddChild = computed(() => {
  return props.menuType === "level" && props.level < 4;
});

// 添加计算属性判断是否应该显示菜单
const shouldShowMenu = computed(() => {
  return props.menuType === "level" && props.visible;
});
// 示例正确的数据结构
const menuStyle = computed(() => ({
  position: "fixed" as const,
  left: props.position.left + "px",
  top: props.position.top + "px",
  zIndex: 999,
}));
// // 修改菜单样式计算
// const menuStyle = computed(() => {
//   if (!shouldShowMenu.value) return {};

//   return {
//     position: "fixed",
//     left: `${props.position?.x}px`,
//     top: `${props.position?.y}px`,
//     zIndex: 2000,
//   };
// });
</script>

<style scoped>
.mini-opt-menu {
  position: fixed;
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.2s ease-in-out;
  transform-origin: top left;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.mini-opt-menu.is-visible {
  opacity: 1;
  transform: scale(1);
}

.menu-card {
  min-width: 150px;
  padding: 4px 0;
  border: none;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.menu-item {
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease-in-out;
  color: #606266;
  font-size: 14px;
  white-space: nowrap;
}

.menu-item:hover {
  background-color: #f5f7fa;
  color: #409eff;
}

.menu-item .el-icon {
  font-size: 16px;
}

.menu-divider {
  margin: 4px 0;
  border-top: 1px solid #ebeef5;
}
/* 添加禁用样式 */
.menu-item.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 指标计数样式 */
.level-count {
  margin-left: 8px;
  font-size: 12px;
  color: #999;
}
</style>
